import React from "react";
import {
  Settings,
  Users,
  Shield,
  Search,
  Camera,
  Building2,
  UserCheck,
  MapPin,
} from "lucide-react";
import { motion } from "framer-motion";
import Image from "../Assets/Design Welcome Family.png";

const ConstructionEcosystemSection = ({ isDarkMode = false }) => {
  const leftFeatures = [
    {
      title: "A Complete Construction Ecosystem",
      icon: <Building2 className="w-8 h-8 text-black-400" />,
      description:
        "Homeegram unites the entire construction & real estate workforce blue collar to white collar on one  platform.",
    },
    {
      title: "Verified Profiles. Trusted Connections.",
      icon: <Shield className="w-8 h-8 text-black-400" />,
      description:
        "In a sector plagued by informal networks, we bring trust & accountability.",
    },
    {
      title: "Work That Finds You",
      icon: <Search className="w-8 h-8 text-black-400" />,
      description:
        "Homeegram connects you to meaningful opportunities without middlemen.",
      highlighted: true,
    },
  ];

  const rightFeatures = [
    {
      title: "Showcase Your Work, Professionally",
      icon: <Camera className="w-8 h-8 text-black-400" />,
      description:
        "Your talent deserves a stage, with Homeegram your profile speaks volumes.",
    },
    {
      title: "Made for Every Construction Role",
      icon: <Users className="w-8 h-8 text-black-400" />,
      description:
        "Homeegram is not just for big firms or consultants it's for everyone who builds.",
    },
    {
      title: "Bridge the Urban Rural Opportunity Gap",
      icon: <MapPin className="w-8 h-8 text-black-400" />,
      description:
        "Talent exists everywhere Homeegram makes sure it gets the spotlight.",
    },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section
      id="features"
      className={`relative overflow-hidden font-['Poppins'] ${
        isDarkMode ? "bg-black" : "bg-white"
      }`}
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2
            className={`text-4xl sm:text-5xl lg:text-6xl font-semibold mb-8 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Why Choose Homeegram?
          </h2>
          <p
            className={`text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
           Building the future of construction networking with innovation, trust & equal opportunity
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 items-center">
          {/* Left Features */}
          <div className="space-y-12 lg:text-right">
            {leftFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex lg:flex-row-reverse items-start space-x-6 lg:space-x-reverse lg:space-x-6 group"
              >
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 400 }}
                  className={`flex-shrink-0 w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-300 ${
                    isDarkMode
                      ? "bg-gray-800 group-hover:bg-gray-700"
                      : "bg-gray-50 group-hover:bg-gray-100"
                  }`}
                >
                  {feature.icon}
                </motion.div>

                <div className="flex-1">
                  <h3
                    className={`text-xl font-semibold mb-3 transition-colors duration-300 ${
                      feature.highlighted
                        ? "text-black-400"
                        : `${
                            isDarkMode ? "text-white" : "text-gray-900"
                          } group-hover:text-black-400`
                    }`}
                  >
                    {feature.title}
                  </h3>
                  <p
                    className={`leading-relaxed ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Center Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex justify-center"
          >
            <motion.img
              src={Image}
              alt="Homeegram Community Welcome"
              className="max-w-full h-auto"
              animate={{ y: [0, -10, 0] }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              whileHover={{ scale: 1.02 }}
            />
          </motion.div>

          {/* Right Features */}
          <div className="space-y-12">
            {rightFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-start space-x-6 group"
              >
                {/* Icon */}
                <motion.div
                  whileHover={{ scale: 1.1, rotate: -5 }}
                  transition={{ type: "spring", stiffness: 400 }}
                  className={`flex-shrink-0 w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-300 ${
                    isDarkMode
                      ? "bg-gray-800 group-hover:bg-gray-700"
                      : "bg-gray-50 group-hover:bg-gray-100"
                  }`}
                >
                  {feature.icon}
                </motion.div>

                {/* Content */}
                <div className="flex-1">
                  <h3
                    className={`text-xl font-semibold mb-3 transition-colors duration-300 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    } group-hover:text-black-400`}
                  >
                    {feature.title}
                  </h3>
                  <p
                    className={`leading-relaxed ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <div className="fixed bottom-8 right-8 z-50">
        <motion.button
          onClick={scrollToTop}
          className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
            isDarkMode
              ? "bg-gray-800 hover:bg-gray-700 text-white"
              : "bg-black hover:bg-gray-800 text-white"
          }`}
          whileHover={{ scale: 1.1, y: -3 }}
          whileTap={{ scale: 0.9 }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1 }}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 15l7-7 7 7"
            />
          </svg>
        </motion.button>
      </div>

      <style jsx>{`
        .h-160 {
          height: 40rem;
        }

        .space-x-reverse > * + * {
          margin-left: 0;
          margin-right: 1.5rem;
        }
      `}</style>
    </section>
  );
};

export default ConstructionEcosystemSection;
